import React, { memo, useRef, useEffect } from 'react';

interface SearchInputMemoProps {
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
}

/**
 * Uncontrolled search input that maintains its own DOM state via a ref
 * This prevents React from touching the input element during re-renders
 * The input only syncs when absolutely necessary and when not focused
 */
const SearchInputMemo = memo(({ value, onChange, isLoading }: SearchInputMemoProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Only sync the ref value when the prop value changes AND input is not focused
  // This allows the input to maintain focus even when parent re-renders
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  // Auto-focus the input when loading finishes (after fetch completes)
  // This restores focus that may have been lost during the re-render
  useEffect(() => {
    if (!isLoading && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value);
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <input
        ref={inputRef}
        type="text"
        onChange={handleChange}
        placeholder="Search in name, description, author, or notes..."
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '16px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
});

SearchInputMemo.displayName = 'SearchInputMemo';

export default SearchInputMemo;
